export interface DashboardStats {
  total_applicants: number;
  active_applications: number;
  pending_reviews: number;
  approved_today: number;
  total_bookings: number;
  todays_appointments: number;
  active_licenses: number;
  system_health: 'excellent' | 'good' | 'warning' | 'critical';
  new_applications: number;
  renewal_applications: number;
}

export interface RecentActivity {
  id: number;
  type: 'application' | 'booking' | 'approval' | 'document' | 'system';
  title: string;
  description: string;
  user: string;
  timestamp: string;
  status?: 'success' | 'warning' | 'error' | 'info';
}

export interface ChartData {
  applications_by_month: {
    month: string;
    submitted: number;
    approved: number;
    rejected: number;
  }[];
  applications_by_status: {
    status: string;
    count: number;
    color: string;
  }[];
  bookings_by_center: {
    center: string;
    count: number;
  }[];
}

export interface AuditLog {
  id: number;
  user_id: number;
  user_name: string;
  action: string;
  entity_type: 'APPLICANT' | 'APPLICATION' | 'BOOKING' | 'CENTER' | 'USER' | 'DOCUMENT';
  entity_id: number;
  details: string;
  ip_address: string;
  timestamp: string;
}

// Dashboard Statistics
export const mockDashboardStats: DashboardStats = {
  total_applicants: 1247,
  active_applications: 89,
  pending_reviews: 23,
  approved_today: 12,
  total_bookings: 456,
  todays_appointments: 18,
  active_licenses: 1089,
  system_health: 'good',
  new_applications: 54,
  renewal_applications: 35,
};

// Recent Activity
export const mockRecentActivity: RecentActivity[] = [
  {
    id: 1,
    type: 'application',
    title: 'New Application Submitted',
    description: 'John Okafor submitted CPL application',
    user: 'John Okafor',
    timestamp: '2025-01-08T10:15:00Z',
    status: 'info',
  },
  {
    id: 2,
    type: 'approval',
    title: 'Application Approved',
    description: 'PPL application #1234 approved by Admin User',
    user: 'Admin User',
    timestamp: '2025-01-08T09:45:00Z',
    status: 'success',
  },
  {
    id: 3,
    type: 'booking',
    title: 'Appointment Booked',
    description: 'Amina Bello booked slot at Lagos Center',
    user: 'Amina Bello',
    timestamp: '2025-01-08T09:30:00Z',
    status: 'info',
  },
  {
    id: 4,
    type: 'document',
    title: 'Document Uploaded',
    description: 'Medical certificate uploaded for review',
    user: 'Emmanuel Eze',
    timestamp: '2025-01-08T09:15:00Z',
    status: 'info',
  },
  {
    id: 5,
    type: 'approval',
    title: 'Application Rejected',
    description: 'ATPL application #5678 rejected - missing documents',
    user: 'Operations Manager',
    timestamp: '2025-01-08T08:50:00Z',
    status: 'warning',
  },
  {
    id: 6,
    type: 'booking',
    title: 'Appointment Cancelled',
    description: 'Booking cancelled by Fatima Ibrahim',
    user: 'Fatima Ibrahim',
    timestamp: '2025-01-08T08:30:00Z',
    status: 'warning',
  },
  {
    id: 7,
    type: 'system',
    title: 'System Backup Completed',
    description: 'Daily backup completed successfully',
    user: 'System',
    timestamp: '2025-01-08T02:00:00Z',
    status: 'success',
  },
  {
    id: 8,
    type: 'document',
    title: 'Document Verified',
    description: 'NIN slip verified for applicant #234',
    user: 'Operations Manager',
    timestamp: '2025-01-07T16:45:00Z',
    status: 'success',
  },
];

// Chart Data
export const mockChartData: ChartData = {
  applications_by_month: [
    { month: 'Jul', submitted: 45, approved: 38, rejected: 5 },
    { month: 'Aug', submitted: 52, approved: 45, rejected: 4 },
    { month: 'Sep', submitted: 67, approved: 58, rejected: 6 },
    { month: 'Oct', submitted: 78, approved: 70, rejected: 5 },
    { month: 'Nov', submitted: 85, approved: 75, rejected: 7 },
    { month: 'Dec', submitted: 92, approved: 82, rejected: 6 },
    { month: 'Jan', submitted: 45, approved: 35, rejected: 3 },
  ],
  applications_by_status: [
    { status: 'Approved', count: 403, color: '#10b981' },
    { status: 'Under Review', count: 89, color: '#f59e0b' },
    { status: 'Pending', count: 23, color: '#6b7280' },
    { status: 'Rejected', count: 36, color: '#ef4444' },
  ],
  bookings_by_center: [
    { center: 'Lagos - Ikeja', count: 145 },
    { center: 'Abuja - Garki', count: 122 },
    { center: 'Port Harcourt', count: 87 },
    { center: 'Kano', count: 62 },
    { center: 'Lagos - VI', count: 40 },
  ],
};

// Audit Logs (for admin dashboard)
export const adminAuditLogs: AuditLog[] = [
  {
    id: 1,
    user_id: 1,
    user_name: 'Admin User',
    action: 'APPROVED_APPLICATION',
    entity_type: 'APPLICATION',
    entity_id: 1,
    details: 'Approved CPL application for John Okafor',
    ip_address: '192.168.1.100',
    timestamp: '2025-01-08T10:30:00Z',
  },
  {
    id: 2,
    user_id: 3,
    user_name: 'Operations Manager',
    action: 'VERIFIED_DOCUMENT',
    entity_type: 'DOCUMENT',
    entity_id: 3,
    details: 'Verified medical certificate for applicant #234',
    ip_address: '192.168.1.101',
    timestamp: '2025-01-08T10:15:00Z',
  },
  {
    id: 3,
    user_id: 1,
    user_name: 'Admin User',
    action: 'CREATED_CENTER',
    entity_type: 'CENTER',
    entity_id: 6,
    details: 'Created new testing center in Enugu',
    ip_address: '192.168.1.100',
    timestamp: '2025-01-08T09:45:00Z',
  },
  {
    id: 4,
    user_id: 3,
    user_name: 'Operations Manager',
    action: 'CANCELLED_BOOKING',
    entity_type: 'BOOKING',
    entity_id: 45,
    details: 'Cancelled booking due to center maintenance',
    ip_address: '192.168.1.101',
    timestamp: '2025-01-08T09:30:00Z',
  },
  {
    id: 5,
    user_id: 1,
    user_name: 'Admin User',
    action: 'UPDATED_USER',
    entity_type: 'USER',
    entity_id: 5,
    details: 'Updated user role from OPS to ADMIN',
    ip_address: '192.168.1.100',
    timestamp: '2025-01-08T09:00:00Z',
  },
];

// Helper functions
export const getDashboardStats = (): DashboardStats => {
  return mockDashboardStats;
};

export const getRecentActivity = (limit?: number): RecentActivity[] => {
  return limit ? mockRecentActivity.slice(0, limit) : mockRecentActivity;
};

export const getChartData = (): ChartData => {
  return mockChartData;
};

export const getAuditLogs = (limit?: number): AuditLog[] => {
  return limit ? adminAuditLogs.slice(0, limit) : adminAuditLogs;
};

export const getSystemHealthColor = (health: DashboardStats['system_health']): string => {
  switch (health) {
    case 'excellent':
      return 'text-green-600';
    case 'good':
      return 'text-blue-600';
    case 'warning':
      return 'text-yellow-600';
    case 'critical':
      return 'text-red-600';
    default:
      return 'text-gray-600';
  }
};
